import { UserOrganizationQuery } from '@/gql/systemApi'
import get from 'lodash/get'

export const generateRandom = (min: number, max: number): number => {
  const difference = max - min
  let rand = Math.random()
  rand = Math.floor(rand * difference)
  rand += min

  return rand
}

export const isValidEmail = (value: string) => {
  const emailRegex = /^\S+@\S+\.\S+$/
  if (!value) {
    return 'Please enter an Email Address'
  }
  if (!emailRegex.test(value)) {
    return 'Please enter a valid Email Address'
  }
  return true
}

export const validatePhoneNumber = (input: string) => {
  if (input === '') {
    return true
  }
  const re = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/

  return re.test(input)
}

export const getFilePath = (id: string | null) =>
  id && `${import.meta.env.VITE_API_URL.replace('graphql', 'assets')}/${id}`

/**
 * Get image path with size
 * @param id - image id
 * @param size - image size in pixels (width and height)
 * @returns string | null - image path if exists
 */
export const getImagePath = ({
  id,
  size,
}: {
  id: string | null
  size: number | null
}) => {
  const filePath = getFilePath(id)
  if (!filePath) return ''
  if (size) {
    return `${filePath}?width=${size}&height=${size}&fit=cover`
  }
  return filePath
}

export const getImageUrlWithParams = ({
  url,
  params,
}: {
  url: string
  params: Record<string, number | string>
}) => {
  const paramString = new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)])
  ).toString()
  // if url has query params, replace them with new params
  // otherwise append new params
  const newUrl = url.includes('?')
    ? `${url.split('?')[0]}?${paramString}`
    : `${url}?${paramString}`
  return newUrl
}

export const getMyOrganizationId = (
  orgData: UserOrganizationQuery | undefined
) => get(orgData, 'users_me.current_organization.[0].organization_id.id')

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`
}

export async function hashStringSHA256(str: string) {
  const encoder = new TextEncoder()
  const data = encoder.encode(str)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

export const getCurrencyString = (money: number) =>
  Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
  }).format(money)

/**
 * Whole-dollar USD with thousands separators and no cents:
 * 9_269 -> "$9,269", 64_285 -> "$64,285".
 * For headline savings figures where trailing ".00" is noise.
 */
export const getWholeDollarCurrencyString = (money: number): string =>
  Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(money)

/**
 * Abbreviated USD currency for compact stat cards.
 * 0 -> "$0", 999 -> "$999", 1_000 -> "$1k", 1_500 -> "$1.5k",
 * 540_000 -> "$540k", 1_000_000 -> "$1M", 1_200_000 -> "$1.2M".
 * Negative values keep the sign: -1_500 -> "-$1.5k".
 */
export const getAbbreviatedCurrencyString = (value: number): string => {
  if (!Number.isFinite(value)) return '$0'

  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)

  const withSuffix = (scaled: number, suffix: string) => {
    // One decimal, but drop a trailing ".0" (1.0M -> "1M", 1.2M stays "1.2M").
    const rounded = Math.round(scaled * 10) / 10
    const text = Number.isInteger(rounded)
      ? String(rounded)
      : rounded.toFixed(1)
    return `${sign}$${text}${suffix}`
  }

  // Promote to the next unit when the one-decimal rounding overflows to 1000
  // (e.g. 999_950 / 1000 = 999.95 rounds to 1000 -> show "$1M", not "$1000k").
  const thousands = Math.round((abs / 1_000) * 10) / 10

  if (abs >= 1_000_000 || thousands >= 1_000)
    return withSuffix(abs / 1_000_000, 'M')
  if (abs >= 1_000) return withSuffix(abs / 1_000, 'k')
  return `${sign}$${Math.round(abs)}`
}
